import { useEffect, useMemo, useState } from 'react';
import { formatXof, translate, type WashedLocale } from '@washed/i18n';
import { CoreApiError } from '@washed/api-client';
import {
  sponsorPayApi,
  type SponsorInviteSummary,
  type SponsorPayApi,
  type SponsorPayPlanCode,
  type SponsorQuote,
} from '../api/sponsor-pay.js';
import {
  currencyMeta,
  detectBrowserCurrency,
  formatCurrency,
  isSupportedCurrency,
  listCurrencies,
  type CurrencyRegion,
} from '../lib/sponsor-currency.js';
import {
  countryRequiresTaxPostalCode,
  detectTaxCountry,
  listTaxCountries,
} from '../lib/sponsor-tax-country.js';
import styles from './SponsorPayPage.module.css';

export interface SponsorPayPageProps {
  readonly api?: SponsorPayApi;
  readonly initialLocale?: WashedLocale;
  readonly initialCurrency?: string;
  readonly initialTaxCountry?: string;
  readonly redirectToCheckout?: (url: string) => void;
  readonly token: string | null;
}

interface SponsorPayContent {
  readonly homeLabel: string;
  readonly langLabel: string;
  readonly eyebrow: string;
  readonly title: (name: string) => string;
  readonly intro: (name: string) => string;
  readonly youPayLabel: string;
  readonly perMonthLabel: string;
  readonly exactNote: string;
  readonly quoteLoadingLabel: string;
  readonly currencyLabel: string;
  readonly taxCountryLabel: string;
  readonly taxPostalCodeLabel: string;
  readonly taxPostalCodePlaceholder: string;
  readonly taxPostalCodeRequiredLabel: string;
  readonly taxIncludedLabel: string;
  readonly detailLabel: string;
  readonly recipientReceivesLabel: (name: string) => string;
  readonly billingNote: string;
  readonly emailLabel: string;
  readonly emailPlaceholder: string;
  readonly methodLabel: string;
  readonly cardLabel: string;
  readonly methodNote: string;
  readonly submitLabel: string;
  readonly submittingLabel: string;
  readonly secureNote: string;
  readonly providerLinkLabel: string;
  readonly footerLinksLabel: string;
  readonly legalPrivacy: string;
  readonly legalTerms: string;
  readonly footerNote: string;
  readonly visualBody: string;
  readonly visualTitleAccent: string;
  readonly visualTitleLead: string;
  readonly loadingLabel: string;
  readonly errorTitle: string;
  readonly errorBody: string;
  readonly retryBody: string;
  readonly invalidTitle: string;
  readonly invalidBody: string;
  readonly expiredTitle: string;
  readonly expiredBody: (name: string) => string;
  readonly regionLabels: Record<CurrencyRegion, string>;
}

const CONTENT: Record<WashedLocale, SponsorPayContent> = {
  fr: {
    homeLabel: 'Accueil',
    langLabel: 'English',
    eyebrow: 'Washed · PayFor',
    title: (name) => `Un service pour ${name}, payé par vous.`,
    intro: () => '',
    youPayLabel: 'Vous payez',
    perMonthLabel: '/ mois',
    exactNote: 'Taux exact confirmé pour ce paiement.',
    quoteLoadingLabel: 'Calcul du montant exact…',
    currencyLabel: 'Devise',
    taxCountryLabel: 'Pays de facturation',
    taxPostalCodeLabel: 'Code postal',
    taxPostalCodePlaceholder: 'Code postal',
    taxPostalCodeRequiredLabel: 'Ajoutez le code postal pour calculer la taxe exacte.',
    taxIncludedLabel: 'Taxe incluse',
    detailLabel: 'Détail du paiement',
    recipientReceivesLabel: (name) => `${name} reçoit`,
    billingNote: 'Mensuel. Annulable à tout moment.',
    emailLabel: 'Votre e-mail',
    emailPlaceholder: 'vous@exemple.com',
    methodLabel: 'Moyen de paiement',
    cardLabel: 'Carte ou Link',
    methodNote: 'Vous continuerez vers Stripe Checkout.',
    submitLabel: 'Continuer',
    submittingLabel: 'Préparation…',
    secureNote: 'Paiement sécurisé. Aucun frais caché.',
    providerLinkLabel: 'Ouvrir le paiement sécurisé',
    footerLinksLabel: 'Liens légaux',
    legalPrivacy: 'Confidentialité',
    legalTerms: 'Conditions',
    footerNote: 'Washed · Paiement sécurisé',
    visualBody:
      'Un paiement clair pour soutenir un proche, sans accès aux détails privés du foyer.',
    visualTitleAccent: 'à la maison.',
    visualTitleLead: 'Le soin arrive',
    loadingLabel: 'Chargement du lien PayFor…',
    errorTitle: 'Paiement indisponible',
    errorBody:
      'Nous n’avons pas pu ouvrir ce paiement. Réessayez dans un instant, ou demandez un nouveau lien depuis l’application.',
    retryBody: 'Le paiement n’a pas pu être préparé. Réessayez dans un instant.',
    invalidTitle: 'Lien incomplet',
    invalidBody:
      'Ce lien PayFor est incomplet. Ouvrez-le à nouveau depuis le message reçu, ou demandez un nouveau lien.',
    expiredTitle: 'Lien expiré',
    expiredBody: (name) =>
      `Ce lien PayFor pour ${name} n’est plus actif. Demandez un nouveau lien depuis l’application.`,
    regionLabels: {
      Europe: 'Europe',
      Americas: 'Amériques',
      Africa: 'Afrique',
      Asia: 'Asie',
      Oceania: 'Océanie',
    },
  },
  en: {
    homeLabel: 'Home',
    langLabel: 'Français',
    eyebrow: 'Washed · PayFor',
    title: (name) => `A service for ${name}, paid by you.`,
    intro: () => '',
    youPayLabel: 'You pay',
    perMonthLabel: '/ mo',
    exactNote: 'Exact rate confirmed for this payment.',
    quoteLoadingLabel: 'Calculating exact amount…',
    currencyLabel: 'Currency',
    taxCountryLabel: 'Billing country',
    taxPostalCodeLabel: 'Postal code',
    taxPostalCodePlaceholder: 'Postal code',
    taxPostalCodeRequiredLabel: 'Add the postal code to calculate exact tax.',
    taxIncludedLabel: 'Tax included',
    detailLabel: 'Payment detail',
    recipientReceivesLabel: (name) => `${name} receives`,
    billingNote: 'Monthly. Cancel anytime.',
    emailLabel: 'Your email',
    emailPlaceholder: 'you@example.com',
    methodLabel: 'Payment method',
    cardLabel: 'Card or Link',
    methodNote: 'You will continue to Stripe Checkout.',
    submitLabel: 'Continue',
    submittingLabel: 'Preparing…',
    secureNote: 'Secure payment. No hidden fees.',
    providerLinkLabel: 'Open secure payment',
    footerLinksLabel: 'Legal links',
    legalPrivacy: 'Privacy',
    legalTerms: 'Terms',
    footerNote: 'Washed · Secure payment',
    visualBody:
      'A clear payment for supporting someone close, without access to private household details.',
    visualTitleAccent: 'home.',
    visualTitleLead: 'Care that reaches',
    loadingLabel: 'Loading PayFor link…',
    errorTitle: 'Payment unavailable',
    errorBody:
      'We couldn’t open this payment. Try again in a moment, or ask for a new link from the app.',
    retryBody: 'The payment couldn’t be prepared. Try again in a moment.',
    invalidTitle: 'Incomplete link',
    invalidBody:
      'This PayFor link is incomplete. Open it again from the message you received, or ask for a new link.',
    expiredTitle: 'Expired link',
    expiredBody: (name) =>
      `This PayFor link for ${name} is no longer active. Ask for a new link from the app.`,
    regionLabels: {
      Europe: 'Europe',
      Americas: 'Americas',
      Africa: 'Africa',
      Asia: 'Asia',
      Oceania: 'Oceania',
    },
  },
};

function planLabel(locale: WashedLocale, code: SponsorPayPlanCode): string {
  if (code === 'S1') return translate('pricing.tiers.s1', locale);
  if (code === 'S2') return translate('pricing.tiers.s2', locale);
  if (code === 'D1') return translate('pricing.tiers.d1', locale);
  if (code === 'D2') return translate('pricing.tiers.d2', locale);
  if (code === 'F1') return translate('pricing.tiers.f1', locale);
  return translate('pricing.tiers.f2', locale);
}

// Failures carry no raw message: API/config errors are developer text, and the
// copy must follow the payer's language even if they switch it afterwards.
type InviteState =
  | { readonly kind: 'error' }
  | { readonly kind: 'invalid' }
  | { readonly kind: 'loading' }
  | { readonly invite: SponsorInviteSummary; readonly kind: 'ready' };

type CheckoutState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading' }
  | { readonly checkoutUrl: string; readonly kind: 'ready' }
  | { readonly kind: 'error' };

type QuoteState =
  | { readonly kind: 'error' }
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly quote: SponsorQuote };

export function SponsorPayPage({
  api = sponsorPayApi,
  initialLocale = 'fr',
  initialCurrency,
  initialTaxCountry,
  redirectToCheckout = (url) => window.location.assign(url),
  token,
}: SponsorPayPageProps) {
  const [locale, setLocale] = useState<WashedLocale>(initialLocale);
  const [currency, setCurrency] = useState<string>(() => {
    if (initialCurrency && isSupportedCurrency(initialCurrency)) return initialCurrency;
    return detectBrowserCurrency();
  });
  const [taxCountry, setTaxCountry] = useState<string>(
    () => normalizeInitialTaxCountry(initialTaxCountry) ?? detectTaxCountry(undefined),
  );
  const [taxPostalCode, setTaxPostalCode] = useState('');
  const [inviteState, setInviteState] = useState<InviteState>({ kind: 'loading' });
  const [quoteState, setQuoteState] = useState<QuoteState>({ kind: 'idle' });
  const [checkoutState, setCheckoutState] = useState<CheckoutState>({ kind: 'idle' });
  const [sponsorEmail, setSponsorEmail] = useState('');
  const c = CONTENT[locale];
  const toggle = () => setLocale(locale === 'fr' ? 'en' : 'fr');
  // The static /payfor/ page is prerendered without a token, while the browser
  // parses the real one from the URL. Rendering the loading shell until mount
  // keeps server HTML and first client render identical (no hydration #418).
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (token === null) return;

    let active = true;
    setInviteState({ kind: 'loading' });
    setQuoteState({ kind: 'idle' });
    setCheckoutState({ kind: 'idle' });

    api
      .getInvite(token)
      .then((invite) => {
        if (active) setInviteState({ kind: 'ready', invite });
      })
      .catch((error: unknown) => {
        if (!active) return;
        console.error('PayFor invite failed to load:', error);
        // 400/404: the API does not recognise this token, so the link itself is bad.
        const linkIsBad =
          error instanceof CoreApiError && (error.status === 400 || error.status === 404);
        setInviteState({ kind: linkIsBad ? 'invalid' : 'error' });
      });

    return () => {
      active = false;
    };
  }, [api, token]);

  useEffect(() => {
    if (
      token === null ||
      inviteState.kind !== 'ready' ||
      inviteState.invite.status !== 'open' ||
      (countryRequiresTaxPostalCode(taxCountry) && taxPostalCode.trim().length === 0)
    ) {
      setQuoteState({ kind: 'idle' });
      return;
    }

    let active = true;
    setQuoteState({ kind: 'loading' });

    api
      .getQuote(token, currency, {
        taxCountry,
        taxPostalCode: taxPostalCode.trim(),
      })
      .then((quote) => {
        if (active) setQuoteState({ kind: 'ready', quote });
      })
      .catch((error: unknown) => {
        if (!active) return;
        console.error('PayFor quote failed:', error);
        setQuoteState({ kind: 'error' });
      });

    return () => {
      active = false;
    };
  }, [api, currency, inviteState, taxCountry, taxPostalCode, token]);

  const pageUrl = useMemo(() => {
    if (typeof window === 'undefined' || token === null) return '';
    return new URL(`/payfor/${encodeURIComponent(token)}`, window.location.origin).toString();
  }, [token]);

  async function submitCheckout(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (
      token === null ||
      inviteState.kind !== 'ready' ||
      inviteState.invite.status !== 'open' ||
      quoteState.kind !== 'ready'
    ) {
      return;
    }

    setCheckoutState({ kind: 'loading' });

    try {
      const session = await api.startCheckout(token, {
        cancelUrl: pageUrl,
        payerCurrency: currency,
        requestedNetwork: 'card',
        returnUrl: new URL(
          `/payfor/${encodeURIComponent(token)}/return`,
          window.location.origin,
        ).toString(),
        sponsorEmail: sponsorEmail.trim().toLowerCase(),
        taxCountry,
        taxPostalCode: taxPostalCode.trim(),
      });
      setCheckoutState({ kind: 'ready', checkoutUrl: session.checkoutUrl });
      redirectToCheckout(session.checkoutUrl);
    } catch (error) {
      console.error('PayFor checkout failed:', error);
      setCheckoutState({ kind: 'error' });
    }
  }

  return (
    <div className={styles.page}>
      <main className={styles.shell} data-payfor-layout="split-shell">
        <section className={styles.paymentPane}>
          <div className={styles.paymentPaneInner}>
            <header className={styles.nav}>
              <a
                className={styles.wordmark}
                data-brand-variant="v6-wordmark"
                href="/"
                aria-label="Washed"
              >
                washed<span className={styles.dot}>.</span>
              </a>
              <div className={styles.navLinks}>
                <a href="/">{c.homeLabel}</a>
                <button type="button" className={styles.langToggle} onClick={toggle}>
                  {c.langLabel}
                </button>
              </div>
            </header>

            <div className={styles.main}>
              {!mounted ? (
                <StatusPanel eyebrow={c.eyebrow} title={c.loadingLabel} body="" />
              ) : token === null || inviteState.kind === 'invalid' ? (
                <StatusPanel eyebrow={c.eyebrow} title={c.invalidTitle} body={c.invalidBody} />
              ) : inviteState.kind === 'loading' ? (
                <StatusPanel eyebrow={c.eyebrow} title={c.loadingLabel} body="" />
              ) : inviteState.kind === 'error' ? (
                <StatusPanel eyebrow={c.eyebrow} title={c.errorTitle} body={c.errorBody} />
              ) : inviteState.invite.status !== 'open' ? (
                <StatusPanel
                  eyebrow={c.eyebrow}
                  title={c.expiredTitle}
                  body={c.expiredBody(inviteState.invite.recipientFirstName)}
                />
              ) : (
                <OpenInviteContent
                  c={c}
                  checkoutState={checkoutState}
                  currency={currency}
                  invite={inviteState.invite}
                  locale={locale}
                  quoteState={quoteState}
                  setCurrency={setCurrency}
                  setSponsorEmail={setSponsorEmail}
                  setTaxCountry={setTaxCountry}
                  setTaxPostalCode={setTaxPostalCode}
                  sponsorEmail={sponsorEmail}
                  submitCheckout={submitCheckout}
                  taxCountry={taxCountry}
                  taxPostalCode={taxPostalCode}
                />
              )}
            </div>

            <footer className={styles.footer}>
              <span className={styles.footerBrand} data-brand-variant="v6-wordmark">
                washed<span className={styles.dot}>.</span>
              </span>
              <nav className={styles.footerLinks} aria-label={c.footerLinksLabel}>
                <a href="/confidentialite">{c.legalPrivacy}</a>
                <a href="/conditions">{c.legalTerms}</a>
              </nav>
              <span className={styles.footerNote}>{c.footerNote}</span>
            </footer>
          </div>
        </section>

        <VisualPanel c={c} />
      </main>
    </div>
  );
}

function VisualPanel({ c }: { readonly c: SponsorPayContent }) {
  return (
    <aside className={styles.visualPane} aria-label={c.eyebrow}>
      <video
        className={styles.visualVideo}
        data-payfor-animation="flow-gradient"
        autoPlay
        loop
        muted
        playsInline
        preload="none"
        poster="/videos/flow_gradient_remix_scene.jpg"
        aria-hidden="true"
      >
        <source src="/videos/flow_gradient_remix_scene.mp4" type="video/mp4" />
      </video>
      <span className={styles.visualPoster} aria-hidden="true" />

      <a
        className={styles.visualBrand}
        data-brand-variant="v6-wordmark"
        href="/"
        aria-label="Washed"
      >
        washed<span className={styles.dot}>.</span>
      </a>

      <div className={styles.visualCopy}>
        <p className={styles.visualTitle}>
          {c.visualTitleLead} <span>{c.visualTitleAccent}</span>
        </p>
        <p className={styles.visualBody}>{c.visualBody}</p>
      </div>
    </aside>
  );
}

function StatusPanel({
  body,
  eyebrow,
  title,
}: {
  readonly body: string;
  readonly eyebrow: string;
  readonly title: string;
}) {
  return (
    <section className={styles.statusPanel}>
      <p className={`smallcaps ${styles.eyebrow}`}>{eyebrow}</p>
      <h1 className={styles.statusTitle}>{title}</h1>
      {body.length > 0 ? <p className={styles.statusBody}>{body}</p> : null}
    </section>
  );
}

function OpenInviteContent({
  c,
  checkoutState,
  currency,
  invite,
  locale,
  quoteState,
  setCurrency,
  setSponsorEmail,
  setTaxCountry,
  setTaxPostalCode,
  sponsorEmail,
  submitCheckout,
  taxCountry,
  taxPostalCode,
}: {
  readonly c: SponsorPayContent;
  readonly checkoutState: CheckoutState;
  readonly currency: string;
  readonly invite: SponsorInviteSummary;
  readonly locale: WashedLocale;
  readonly quoteState: QuoteState;
  readonly setCurrency: (code: string) => void;
  readonly setSponsorEmail: (email: string) => void;
  readonly setTaxCountry: (code: string) => void;
  readonly setTaxPostalCode: (postalCode: string) => void;
  readonly sponsorEmail: string;
  readonly submitCheckout: (event: React.FormEvent<HTMLFormElement>) => Promise<void>;
  readonly taxCountry: string;
  readonly taxPostalCode: string;
}) {
  const intlLocale = locale === 'fr' ? 'fr-FR' : 'en-US';
  const planName = planLabel(locale, invite.planCode);
  const intro = c.intro(invite.recipientFirstName);
  const grouped = useMemo(() => groupCurrencies(), []);
  const taxCountries = useMemo(() => listTaxCountries(locale), [locale]);
  const taxPostalRequired = countryRequiresTaxPostalCode(taxCountry);
  const amountText =
    quoteState.kind === 'ready'
      ? formatSponsorQuoteTotal(quoteState.quote, intlLocale)
      : taxPostalRequired && taxPostalCode.trim().length === 0
        ? c.taxPostalCodeRequiredLabel
        : c.quoteLoadingLabel;

  return (
    <section className={styles.checkout}>
      <article className={styles.card}>
        <div className={styles.cardSheen} aria-hidden="true" />

        <header className={styles.cardHead}>
          <span className={`smallcaps ${styles.chip}`}>{c.eyebrow}</span>
          <h1 className={styles.title}>{c.title(invite.recipientFirstName)}</h1>
          <p className={styles.planLine}>
            {planName} · {invite.planCode}
          </p>
          {intro.length > 0 ? <p className={styles.intro}>{intro}</p> : null}
        </header>

        <section className={styles.amount} aria-label={c.detailLabel}>
          <div className={styles.amountTop}>
            <span className={styles.amountLabel}>{c.youPayLabel}</span>
            <div className={styles.currencyPick}>
              <select
                aria-label={c.currencyLabel}
                onChange={(event) => setCurrency(event.target.value)}
                value={currency}
              >
                {grouped.map(([region, items]) => (
                  <optgroup key={region} label={c.regionLabels[region]}>
                    {items.map((m) => (
                      <option key={m.code} value={m.code}>
                        {m.code} — {m.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <span className={styles.currencyValue} aria-hidden="true">
                {currency}
                <ChevronMark />
              </span>
            </div>
          </div>
          <div className={styles.taxLocation}>
            <label className={styles.compactField}>
              <span>{c.taxCountryLabel}</span>
              <select
                onChange={(event) => {
                  setTaxCountry(event.target.value);
                  setTaxPostalCode('');
                }}
                value={taxCountry}
              >
                {taxCountries.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.name}
                  </option>
                ))}
              </select>
            </label>
            {taxPostalRequired ? (
              <label className={styles.compactField}>
                <span>{c.taxPostalCodeLabel}</span>
                <input
                  autoComplete="postal-code"
                  onChange={(event) => setTaxPostalCode(event.target.value)}
                  placeholder={c.taxPostalCodePlaceholder}
                  value={taxPostalCode}
                />
              </label>
            ) : null}
          </div>
          <div className={styles.amountValue}>
            <b className={styles.amountNumber}>{amountText}</b>
            <span className={styles.perMonth}>{c.perMonthLabel}</span>
          </div>
          <p className={styles.rateNote}>{c.exactNote}</p>
          {quoteState.kind === 'error' ? (
            <p className={styles.errorText} role="alert">
              {c.retryBody}
            </p>
          ) : null}

          <div className={styles.ledger}>
            {quoteState.kind === 'ready' ? (
              <div className={styles.ledgerRow}>
                <span>{c.taxIncludedLabel}</span>
                <b className="mono">{formatSponsorQuoteTax(quoteState.quote, intlLocale)}</b>
              </div>
            ) : null}
            <div className={`${styles.ledgerRow} ${styles.ledgerTotal}`}>
              <span>{c.recipientReceivesLabel(invite.recipientFirstName)}</span>
              <b className="mono">{formatXof(invite.planAmountMinor, locale)}</b>
            </div>
          </div>
          <p className={styles.billingNote}>{c.billingNote}</p>
        </section>

        <form className={styles.form} onSubmit={(event) => void submitCheckout(event)}>
          <label className={styles.field}>
            <span>{c.emailLabel}</span>
            <input
              autoComplete="email"
              inputMode="email"
              onChange={(event) => setSponsorEmail(event.target.value)}
              placeholder={c.emailPlaceholder}
              required
              type="email"
              value={sponsorEmail}
            />
          </label>

          <fieldset className={styles.methodGroup}>
            <legend>{c.methodLabel}</legend>
            <div className={styles.methodOptions}>
              <div className={styles.methodOption} data-selected="true">
                <span className={styles.methodMark} aria-hidden="true">
                  <CardMark />
                </span>
                <span className={styles.methodLabelText}>{c.cardLabel}</span>
                <span className={styles.methodNote}>{c.methodNote}</span>
              </div>
            </div>
          </fieldset>

          <button
            className={styles.submit}
            disabled={checkoutState.kind === 'loading' || quoteState.kind !== 'ready'}
            type="submit"
          >
            <LockMark />
            <span>{checkoutState.kind === 'loading' ? c.submittingLabel : c.submitLabel}</span>
          </button>
          <p className={styles.secureNote}>{c.secureNote}</p>

          {checkoutState.kind === 'error' ? (
            <p className={styles.errorText} role="alert">
              {c.retryBody}
            </p>
          ) : null}
          {checkoutState.kind === 'ready' ? (
            <a className={styles.providerLink} href={checkoutState.checkoutUrl}>
              {c.providerLinkLabel}
            </a>
          ) : null}
        </form>
      </article>
    </section>
  );
}

function groupCurrencies(): readonly [
  CurrencyRegion,
  ReturnType<typeof listCurrencies>[number][],
][] {
  const order: readonly CurrencyRegion[] = ['Europe', 'Americas', 'Africa', 'Asia', 'Oceania'];
  return order
    .map(
      (region) =>
        [region, listCurrencies().filter((m) => m.region === region)] as [
          CurrencyRegion,
          ReturnType<typeof listCurrencies>[number][],
        ],
    )
    .filter(([, items]) => items.length > 0);
}

function formatSponsorQuoteTotal(quote: SponsorQuote, locale: string): string {
  const meta = currencyMeta(quote.currency);
  const divisor = meta?.decimals === 0 ? 1 : 100;
  return formatCurrency(quote.totalAmountMinor / divisor, quote.currency, locale);
}

function formatSponsorQuoteTax(quote: SponsorQuote, locale: string): string {
  const meta = currencyMeta(quote.currency);
  const divisor = meta?.decimals === 0 ? 1 : 100;
  return formatCurrency(quote.taxAmountInclusiveMinor / divisor, quote.currency, locale);
}

function normalizeInitialTaxCountry(countryCode: string | undefined): string | null {
  if (countryCode === undefined) return null;
  const normalized = countryCode.trim().toUpperCase();
  return /^[A-Z]{2}$/u.test(normalized) ? normalized : null;
}

function CardMark() {
  return (
    <svg className={styles.cardMark} viewBox="0 0 28 20" fill="none" aria-hidden="true">
      <rect
        x="1.1"
        y="1.1"
        width="25.8"
        height="17.8"
        rx="3.6"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path d="M1.1 6.4h25.8" stroke="currentColor" strokeWidth="3" />
      <rect x="4.4" y="12.4" width="8" height="2.4" rx="1.2" fill="currentColor" />
    </svg>
  );
}

function LockMark() {
  return (
    <svg className={styles.lockMark} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect
        x="5"
        y="10.4"
        width="14"
        height="9.2"
        rx="2.2"
        stroke="currentColor"
        strokeWidth="1.7"
      />
      <path d="M8 10.4V8a4 4 0 0 1 8 0v2.4" stroke="currentColor" strokeWidth="1.7" />
    </svg>
  );
}

function ChevronMark() {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M4 6.5 8 10l4-3.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function sponsorTokenFromPath(pathname: string): string | null | undefined {
  const trimmed = pathname.replace(/\/+$/u, '') || '/';
  const segments = trimmed.split('/').filter(Boolean);
  const root = segments[0];

  if (root !== 'payfor' && root !== 'sponsor' && root !== 'sponsor-pay') return undefined;
  if (segments.length !== 2) return null;

  const token = segments[1];
  if (token === undefined) return null;
  let decoded: string;
  try {
    decoded = decodeURIComponent(token);
  } catch {
    return null;
  }
  return /^[A-Za-z0-9_-]{20,200}$/u.test(decoded) ? decoded : null;
}
